import React, { useMemo } from 'react'

import remarkGfm from 'remark-gfm'
import remark2rehype from 'remark-rehype'
import { twMerge } from 'tailwind-merge'

import Markdown from '@/components/markdown/Markdown'

import type { MarkdownNodeProps } from '@/types/components/Markdown'

export const COMPONENTS: Record<string, React.FC<MarkdownNodeProps>> = {
  a: ({ node, ...props }: MarkdownNodeProps) => (
    <a
      {...props}
      target="_blank"
      rel="noreferrer"
      className="text-blue-600 dark:text-blue-400 hover:opacity-70"
    />
  ),
  code: ({ node, ...props }: MarkdownNodeProps) => (
    <span
      {...props}
      className="bg-yellow-100/50 dark:bg-yellow-900/30 rounded-sm px-0.5"
    />
  ),
  ul: ({ node, ...props }: MarkdownNodeProps) => (
    <ul {...props} className="pl-4 list-disc" />
  ),
  ol: ({ node, ...props }: MarkdownNodeProps) => (
    <ol {...props} className="pl-4 list-decimal" />
  ),
  h1: ({ node, ...props }: MarkdownNodeProps) => (
    <span {...props} className="font-medium" />
  ),
  h2: ({ node, ...props }: MarkdownNodeProps) => (
    <span {...props} className="font-medium" />
  ),
  h3: ({ node, ...props }: MarkdownNodeProps) => (
    <span {...props} className="font-medium" />
  ),
  h4: ({ node, ...props }: MarkdownNodeProps) => (
    <span {...props} className="font-medium" />
  ),
  h5: ({ node, ...props }: MarkdownNodeProps) => (
    <span {...props} className="font-medium" />
  ),
  h6: ({ node, ...props }: MarkdownNodeProps) => (
    <span {...props} className="font-medium" />
  ),
}

interface Highlight {
  children?: string
  className?: string
  clickClassName?: string
  onClick?: (value: string) => void
}

export const Highlight: React.FC<Highlight> = ({
  children,
  className,
  clickClassName,
  onClick,
}) => {
  const components = useMemo(() => {
    if (onClick && children) {
      return {
        ...COMPONENTS,
        code: ({ node, ...props }: MarkdownNodeProps) => {
          const firstChild = node?.children[0]
          if (firstChild && firstChild.type === 'text') {
            const value = firstChild.value
            return (
              <span
                {...props}
                onClick={() => onClick(value)}
                className={twMerge(
                  'cursor-pointer bg-yellow-100/50 dark:bg-yellow-900/30 rounded-sm px-0.5 hover:bg-yellow-200/60 dark:hover:bg-yellow-800/40',
                  clickClassName,
                )}
              />
            )
          }
          // default
          return (
            <span
              {...props}
              className="bg-yellow-100/50 dark:bg-yellow-900/30 rounded-sm px-0.5"
            />
          )
        },
      }
    }
    return COMPONENTS
  }, [children, onClick, clickClassName])

  return (
    <Markdown
      rehypePlugins={[remark2rehype]}
      remarkPlugins={[remarkGfm]}
      className={twMerge(
        'text-sm text-primary-900 dark:text-primary-100',
        className,
      )}
      components={components}
      logError={false}
    >
      {children}
    </Markdown>
  )
}

export default Highlight
